import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created. Let's build your intelligence profile.");
      nav("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />
      <div className="max-w-md mx-auto pt-20 px-6 pb-20">
        <div className="overline">Begin</div>
        <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Create your profile.</h1>
        <p className="mt-3 text-stone-600">Takes 30 seconds. Verification and assessment come next.</p>
        <form onSubmit={submit} className="mt-10 space-y-5">
          {[
            { k: "full_name", l: "Full name", t: "text" },
            { k: "email", l: "Email", t: "email" },
            { k: "phone", l: "Phone (WhatsApp)", t: "tel" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-xs uppercase tracking-widest text-stone-500">{f.l}</label>
              <input
                data-testid={`signup-${f.k}-input`}
                required
                type={f.t}
                value={form[f.k]}
                onChange={upd(f.k)}
                className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white focus:border-[#1E1B4B]"
              />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Password</label>
            <div className="relative mt-2">
              <input
                data-testid="signup-password-input"
                required
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={upd("password")}
                className="w-full border border-stone-300 rounded-xl px-4 py-3 pr-12 bg-white focus:border-[#1E1B4B]"
              />
              <button
                type="button"
                data-testid="signup-password-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-stone-500 hover:text-[#1E1B4B]"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button data-testid="signup-submit-btn" disabled={loading}
            className="btn-pill w-full text-white py-3.5" style={{ background: "#1E1B4B" }}>
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="mt-8 text-sm text-stone-500">Already registered? <Link to="/login" className="text-[#1E1B4B] underline underline-offset-4">Sign in</Link></p>
      </div>
    </div>
  );
}
